import React, { useCallback, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { capitalize, debounce, TextField, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha } from '@mui/material/styles';
import { Highlight, useHits, useSearchBox } from 'react-instantsearch';

import SearchIcon from '~/assets/icons/SearchIcon';
import { GET_HAS_NEWS } from '~/graphql/local';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import { Query } from '~/types';

const QuerySuggestions = ({
  value,
  onChange,
  onKeyPress,
  inputRef,
  withAutocomplete,
  search,
  placeholder,
}) => {
  const { t } = useTenantTranslation();
  const { hits } = useHits<{ query: string }>();
  const { refine: refineHits } = useSearchBox();
  const validQuery = value.length > 1;
  const theme = useTheme();

  const { data: newsData } = useQuery<Pick<Query, 'hasNews'>>(GET_HAS_NEWS);
  const hasNews = newsData?.hasNews;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedRefineHits = useCallback(debounce(refineHits, 500), [refineHits]);

  useEffect(() => {
    if (withAutocomplete && validQuery) {
      debouncedRefineHits(value);
    }
  }, [validQuery, debouncedRefineHits, withAutocomplete, value]);

  return (
    <Autocomplete
      data-testid="searchBox"
      options={validQuery && withAutocomplete ? hits : []}
      popupIcon={null}
      // @ts-ignore
      getOptionLabel={(option) => option?.query || ''}
      disablePortal
      inputValue={value}
      onInputChange={onChange}
      onChange={(e, option) => {
        // @ts-ignore
        search(option?.query || '');
      }}
      PaperComponent={(params) => (
        // @ts-ignore
        <StyledGroup {...params} hasOptions={params.children?.filter(Boolean).length > 0} />
      )}
      freeSolo
      renderOption={(params, option) => (
        // @ts-ignore
        <StyledOption {...params}>
          <StyledSearchIcon color={theme.palette.common.icon.autocompleteSearch} />
          <Highlight hit={option} attribute="query" />
        </StyledOption>
      )}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={
            (placeholder && capitalize(placeholder)) ||
            t(hasNews ? 'page.discover.searchAll' : 'page.discover.searchAllWithoutInsights')
          }
          inputProps={{
            ...params?.inputProps,
            'aria-label': 'enter key words',
          }}
          onKeyPress={onKeyPress}
          inputRef={inputRef}
          hasValue={value.length > 0}
        />
      )}
    />
  );
};

export default QuerySuggestions;

const StyledGroup = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasOptions',
})<{ hasOptions: boolean }>`
  position: relative;
  top: 5px;
  padding-top: ${TINY_MARGIN_PX};
  padding-bottom: ${TINY_MARGIN_PX};
  border-radius: 8px;
  box-shadow: 0 3px 6px ${({ theme }) => alpha(theme.palette.common.blackPure, 0.4)};
  display: ${({ hasOptions }) => !hasOptions && 'none'};

  & .MuiAutocomplete-listbox {
    & .MuiAutocomplete-option[aria-selected='true'] {
      &.Mui-focused {
        background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
      }
    }

    &::-webkit-scrollbar {
      width: 18px;
    }

    &::-webkit-scrollbar-track {
      box-shadow: inset 0 0 18px 18px ${({ theme }) => theme.palette.common.scrollbar.track};
      border: solid 7px transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 18px;
      box-shadow: inset 0 0 18px 18px ${({ theme }) => theme.palette.common.scrollbar.thumb};
      border: solid 7px transparent;
    }
  }
`;
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasValue',
})<{ hasValue: boolean }>`
  width: 100%;

  & .MuiAutocomplete-input {
    padding-left: ${({ hasValue }) => (hasValue ? SUBMIDDLE_MARGIN_PX : 0)} !important;
    transition: padding-left 0.2s;
    color: ${({ theme }) => theme.palette.common.input.searchColor};
  }

  & .MuiOutlinedInput-root {
    padding-left: 0;

    & fieldset {
      border-color: transparent;
    }

    &:hover fieldset {
      border-color: transparent;
    }

    &.Mui-focused fieldset {
      border-color: transparent;
    }
  }
`;
const StyledOption = styled('div')`
  & mark {
    color: ${({ theme }) => theme.palette.common.white};
    background: ${({ theme }) => theme.palette.common.highlightBg};
  }
`;
const StyledSearchIcon = styled(SearchIcon)`
  width: 14px;
  height: 14px;
  margin-right: ${TINY_MARGIN_PX};
`;
